import { useEffect, useMemo, useState } from 'react';
import type { TaskBodyProps } from './index';
import { DesignDxTaskLayout, PastSubmissions } from './design-dx/DesignDxTaskLayout';
import { EDITABLE_DX_DRAFT, useDxDraftRequiredHint } from './design-dx/useDxDraftRequiredHint';
import { useFinalReviewHandBack } from './design-dx/useFinalReviewHandBack';
import { designLoopActions } from './design-dx/designLoop';
import { RequestClarificationAction } from './RequestClarificationAction';

const MAX_LEN = 10_000;
const PLACEHOLDER = 'Please paste here all instructions given to the manufacturer';
const PRE_APPROVE_ACTION = 'design-pre-approved';

type SubmissionsTabProps = Pick<TaskBodyProps, 'onPayloadChange'> & {
    patientId: string;
    macro: string;
};

function SubmissionsTab({ patientId, macro, onPayloadChange }: SubmissionsTabProps) {
    const [designPlan, setDesignPlan] = useState('');
    const trimmed = designPlan.trim();
    const valid = trimmed.length > 0 && trimmed.length <= MAX_LEN;
    const dxDraftHint = useDxDraftRequiredHint(patientId, EDITABLE_DX_DRAFT);
    const handBack = useFinalReviewHandBack(patientId);
    const handBackTarget = handBack?.target;
    const designHandedBack = handBackTarget === 'design' || handBackTarget === 'both';
    const { submitTypes, unchangedAction } = designLoopActions(macro);

    useEffect(() => {
        onPayloadChange?.({
            payload: valid ? { designPlan: trimmed } : null,
            valid,
            disabledActions: dxDraftHint ? { [PRE_APPROVE_ACTION]: dxDraftHint } : undefined,
            hiddenActions: designHandedBack ? undefined : [unchangedAction]
        });
    }, [trimmed, valid, dxDraftHint, designHandedBack, unchangedAction, onPayloadChange]);

    const overLimit = trimmed.length > MAX_LEN;

    return (
        <div className="flex-1 min-h-0 flex">
            <div className="flex-[6] min-w-0 flex flex-col font-inter">
                <div className="px-5 h-12 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                    <span className="font-bold text-sm text-gray-800">
                        Treatment design instructions
                    </span>
                    <span
                        className={`text-[11px] tabular-nums ${overLimit ? 'text-red-500' : 'text-gray-400'}`}
                    >
                        {trimmed.length.toLocaleString()} / {MAX_LEN.toLocaleString()}
                    </span>
                </div>
                <textarea
                    value={designPlan}
                    onChange={(e) => setDesignPlan(e.target.value)}
                    placeholder={PLACEHOLDER}
                    spellCheck
                    className="flex-1 min-h-0 w-full px-5 py-4 bg-transparent text-sm text-gray-700 placeholder-gray-400 leading-relaxed resize-none outline-none focus:bg-gray-50/60 transition-colors"
                />
            </div>

            <div className="flex-[4] min-w-0 min-h-0 flex flex-col border-l border-gray-200">
                <PastSubmissions
                    patientId={patientId}
                    submitTypes={submitTypes}
                    handBack={handBack}
                />
            </div>
        </div>
    );
}

export function SubmitDesignPlanTask({
    workCase: c,
    statusMeta,
    onPayloadChange,
    onTaskComplete,
    onRegisterBeforeClose
}: TaskBodyProps) {
    const handBack = useFinalReviewHandBack(c.id);
    const submissions = useMemo(
        () => (
            <SubmissionsTab
                patientId={c.id}
                macro={statusMeta.macro}
                onPayloadChange={onPayloadChange}
            />
        ),
        [c.id, statusMeta.macro, onPayloadChange]
    );
    const requestClarification = useMemo(
        () => <RequestClarificationAction workCase={c} onTaskComplete={onTaskComplete} />,
        [c, onTaskComplete]
    );
    return (
        <DesignDxTaskLayout
            workCase={c}
            statusMeta={statusMeta}
            submissions={submissions}
            onRegisterBeforeClose={onRegisterBeforeClose}
            handBack={handBack}
            revisionsHeaderAction={requestClarification}
        />
    );
}
